import axios from 'axios';
import request from 'superagent';

import { toast } from 'react-toastify';
import { AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { restHost } from '../../apiConfig';
import restClient from '../../services/rest';
import {
  deleteToken,
  formatUrl,
  getApiErrorMessage,
  getCookie,
  jsonToFormUrlEncoded,
} from '../../shared/utils';
import { DashboardUser } from '../dashboardUser/dashboardUserTypes';
import {
  Admin,
  FacilityAdminRegisterData,
  FacilityInfo,
  Facility,
  FacilityStore,
  FacilityStoreActionTypes,
  FacilityAdminInvite,
  NewFacility,
  UpdateFacility,
  UpdateParentFacility,
  FacilityDevice,
  FacilityDeviceAssociationAction,
  Caregiver,
  CaregiverDetails,
  UpdateCaregiverData,
  NewCaregiverData,
  ParentFacility,
  AddParentFacilityAdminData,
  CreateParentFacilityData,
  CreateLocationData,
  BreadcrumbItem,
  Staff,
  Patient,
  Device,
  Note,
  FacilityUpdateType,
  FacilitySurveyInsights,
  FacilitySurveyInsightsState,
  FetchFacilitySurveyInsightsFailureAction,
  FetchFacilitySurveyInsightsSuccessAction,
  RequestFacilitySurveyInsightsAction,
} from './facilityTypes';
import type { SurveyInsightsGranularity } from '../hubOwner/types';

export const apiError = (error: any) => ({
  type: FacilityStoreActionTypes.FACILITY_API_ERR,
  payload: { error: error },
});

export const setLoadState = (loadState: boolean) => ({
  type: FacilityStoreActionTypes.FACILITY_SET_LOAD_STATE,
  payload: { isLoading: loadState },
});

export const setSuccessAlert = (showAlert: boolean) => ({
  type: FacilityStoreActionTypes.SET_SUCCESS_ALERT,
  payload: { showSuccessAlert: showAlert },
});
export const setDuplicateAlert = (showDuplicateAlert: boolean) => ({
  type: FacilityStoreActionTypes.SET_DUPLICATE_ALERT,
  payload: { showDuplicateAlert: showDuplicateAlert },
});

export const setDuplicateAlertFacility = (
  showDuplicateAlertFacility: boolean,
) => ({
  type: FacilityStoreActionTypes.SET_DUPLICATE_ALERT_FACILITY,
  payload: { showDuplicateAlertFacility: showDuplicateAlertFacility },
});

export const setDuplicateAlertCareTeam = (
  showDuplicateAlertCareTeam: boolean,
) => ({
  type: FacilityStoreActionTypes.SET_DUPLICATE_ALERT_CARETEAM,
  payload: { showDuplicateAlertCareTeam: showDuplicateAlertCareTeam },
});

export const setErrorAlert = (showAlert: boolean, error_msg?: string) => ({
  type: FacilityStoreActionTypes.SET_ERROR_ALERT,
  payload: { showErrorAlert: showAlert, error_msg },
});

export const updateFacilityDeviceStatus = (
  serial_number: string,
  status: string,
) => ({
  type: FacilityStoreActionTypes.UPDATE_FACILITY_DEVICE_STATUS,
  payload: { serial_number, status },
});

export const populateParentFacilities = (
  parentFacilities: ParentFacility[],
) => ({
  type: FacilityStoreActionTypes.POPULATE_PARENT_FACILITIES,
  payload: { parentFacilities },
});

export const setAddParentFacilityAdminStatus = (success: boolean | null) => ({
  type: FacilityStoreActionTypes.ADD_PARENT_FACILITY_ADMIN_STATUS,
  payload: { success },
});

export const setFacilitiesBreadcrumbs = (breadcrumbs: BreadcrumbItem[]) => ({
  type: FacilityStoreActionTypes.SET_FACILITIES_BREADCRUMBS,
  payload: breadcrumbs,
});

export const populateStaffList = (staffList: Staff[]) => ({
  type: FacilityStoreActionTypes.POPULATE_STAFF_LIST,
  payload: { staffList },
});

export const populatePatientsList = (patientsList: Patient[]) => ({
  type: FacilityStoreActionTypes.POPULATE_PATIENTS_LIST,
  payload: { patientsList },
});

export const populateDevicesList = (devicesList: Device[]) => ({
  type: FacilityStoreActionTypes.POPULATE_DEVICES_LIST,
  payload: { devicesList },
});

export const populateNotesList = (notesList: Note[]) => ({
  type: FacilityStoreActionTypes.POPULATE_NOTES_LIST,
  payload: { notesList },
});

export const addNoteAction = (note: Note) => ({
  type: FacilityStoreActionTypes.ADD_NOTE,
  payload: { note },
});
export const setNoteActionStatus = (success: boolean, message: string) => ({
  type: FacilityStoreActionTypes.SET_NOTE_ACTION_STATUS,
  payload: { success, message },
});
export const clearNoteActionStatus = () => ({
  type: FacilityStoreActionTypes.CLEAR_NOTE_ACTION_STATUS,
});

export const setDeactivateFacilityStatus = (success: boolean) => ({
  type: FacilityStoreActionTypes.DEACTIVATE_FACILITY_STATUS,
  payload: { success },
});
export const clearDeactivateFacilityStatus = () => ({
  type: FacilityStoreActionTypes.CLEAR_DEACTIVATE_FACILITY_STATUS,
});

export const setEditStaffStatus = (success: boolean | null) => ({
  type: FacilityStoreActionTypes.EDIT_STAFF_STATUS,
  payload: { success },
});

export const createFacilityAdmin = (data: FacilityAdminRegisterData) => {
  let params = jsonToFormUrlEncoded(data);

  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));

    request
      .post(formatUrl(restHost, 'register')) // Specify POST method and URL
      .set('Content-Type', 'application/x-www-form-urlencoded') // Set headers
      .send(params)
      .then((response) => {
        if (response && response.status === 200) {
          dispatch(setSuccessAlert(true));
          dispatch(setLoadState(false));
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 409) {
          dispatch(apiError('Account already exists'));
          dispatch(setDuplicateAlert(true));
          dispatch(setLoadState(false));
        } else if (error.response && error.response.status === 406) {
          dispatch(apiError('Facility account already exists'));
          dispatch(setDuplicateAlertFacility(true));
          dispatch(setLoadState(false));
        } else if (error.response && error.response.status === 405) {
          dispatch(apiError('Care team account already exists'));
          dispatch(setDuplicateAlertCareTeam(true));
          dispatch(setLoadState(false));
        } else {
          dispatch(apiError(error));
          dispatch(setErrorAlert(true));
          dispatch(setLoadState(false));
        }
      }); // Send the form data
  };
};

export const addFacility = (facility: NewFacility) => {
  return (
    dispatch: ThunkDispatch<FacilityStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    return axios
      .post(formatUrl(restHost, 'eng/facility'), facility, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then(() => {
        dispatch(getFacilities());
        dispatch(facilityAdded());
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const updateFacility = (
  facility_id: number,
  facility: UpdateFacility,
  engOrCustomerCare: boolean = false,
  updateType?: FacilityUpdateType,
) => {
  return (
    dispatch: ThunkDispatch<FacilityStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    return axios
      .put(formatUrl(restHost, `facility/${facility_id}`), facility, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then(() => {
        // Handle refresh based on update type first
        if (
          updateType === 'LOCATION_MED_WINDOW' ||
          updateType === 'LOCATION_INFO'
        ) {
          dispatch(getParentFacilities());
        } else {
          // Only apply engOrCustomerCare logic if not a location med window update
          if (engOrCustomerCare) {
            dispatch(getFacilities());
          } else {
            dispatch(getFacilityById(facility_id));
          }
        }
        dispatch(setLoadState(false));
        return true;
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        // The facility table has a UNIQUE(address, city, state) constraint
        // (migration 0111) that also covers deactivated rows, so a colliding
        // address returns a raw Postgres error. Show a customer-care-actionable
        // message instead of the raw constraint text.
        const raw = err?.response?.data?.message ?? '';
        const isAddressCollision =
          err?.response?.status === 409 || /unique_add/.test(raw);
        toast.error(
          isAddressCollision
            ? 'This address is already in use by another location (possibly a deactivated one). Contact engineering to free it.'
            : raw,
        );
        dispatch(apiError(err));
        dispatch(setLoadState(false));
        return false;
      });
  };
};

export const facilityAdded = () => ({
  type: FacilityStoreActionTypes.FACILITY_ADDED,
});

export const getFacilities = () => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, 'eng/facility'), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        dispatch(populateFacilities(res.data));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const getFacilityDevices = (
  facilityId: number,
  isFromModernFacility: boolean = false, // Skip loading state for modern facility UI to use
) => {
  return (dispatch: Dispatch) => {
    if (!isFromModernFacility) {
      dispatch(setLoadState(true));
    }
    return axios
      .get(formatUrl(restHost, `facility/${facilityId}/devices`), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        dispatch(populateFacilityDevices(facilityId, res.data));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const getFacilityById = (facilityId: number) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, `facility/${facilityId}/info`), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        dispatch(populateFacilityInfo(res.data));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const populateFacilities = (facilities: FacilityInfo[]) => ({
  type: FacilityStoreActionTypes.POPULATE_FACILITIES,
  payload: { facilities },
});

export const populateFacilityDevices = (
  facility_id: number,
  devices: FacilityDevice[],
) => ({
  type: FacilityStoreActionTypes.POPULATE_FACILITY_DEVICES,
  payload: {
    facilityDevices: {
      facility_id,
      devices,
    },
  },
});

export const clearFacilityDevices = () => ({
  type: FacilityStoreActionTypes.CLEAR_FACILITY_DEVICES,
});

export const populateFacilityInfo = (facilityInfo: Facility) => ({
  type: FacilityStoreActionTypes.POPULATE_FACILITY_INFO,
  payload: { facilityInfo },
});

export const setFacilityAdmins = (facilityId: number, admins: Admin[]) => ({
  type: FacilityStoreActionTypes.SET_FACILITY_ADMINS,
  payload: { facilityId: facilityId, admins: admins },
});

export const getFacilityAdmins = (facilityId: number) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, 'eng/facility/' + facilityId + '/customers'), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        let admins = res.data.map((admin: DashboardUser) => {
          return {
            name: `${admin.first_name} ${admin.last_name}`,
            email: admin.email,
          };
        });
        dispatch(setFacilityAdmins(facilityId, admins));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const setFacilityInvites = (
  facilityId: number,
  invites: FacilityAdminInvite[],
) => ({
  type: FacilityStoreActionTypes.SET_FACILITY_INVITES,
  payload: { facilityId: facilityId, invites: invites },
});

export const getFacilityInvites = (facilityId: number) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, 'eng/facility/' + facilityId + '/invite'), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        dispatch(setFacilityInvites(facilityId, res.data));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const createFacilityInvite = (
  facilityId: number,
  data: FacilityAdminInvite,
) => {
  let queryParams = jsonToFormUrlEncoded(data);
  return (
    dispatch: ThunkDispatch<FacilityStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    return axios
      .post(
        formatUrl(restHost, 'eng/facility/' + facilityId + '/invite'),
        queryParams,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      )
      .then((response: any) => {
        if (response.status === 200) {
          dispatch(getFacilityInvites(facilityId));
          toast.success('Invite sent successfully', { autoClose: 3000 });
          dispatch(setLoadState(false));
          dispatch(setAddParentFacilityAdminStatus(true));
          return;
        } else {
          toast.error(response?.response?.data || 'Failed to send invite', {
            autoClose: 3000,
          });
          dispatch(setLoadState(false));
          dispatch(setAddParentFacilityAdminStatus(false));
        }
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        dispatch(apiError(err));
        dispatch(setLoadState(false));
        toast.error(err?.response?.data?.message, {
          autoClose: 3000,
        });
        dispatch(setAddParentFacilityAdminStatus(false));
      });
  };
};

export const deleteFacilityInvite = (
  facilityId: number,
  data: FacilityAdminInvite,
) => {
  let queryParams = jsonToFormUrlEncoded(data);
  return (
    dispatch: ThunkDispatch<FacilityStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    return axios
      .delete(formatUrl(restHost, 'eng/facility/' + facilityId + '/invite'), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
        data: queryParams,
      })
      .then((response) => {
        // Tried to delete an invite that's already accepted
        if (response.status === 200) {
          toast.error(response.data.message);
        } else {
          // 204 No problem while deleting invite
          toast.success('Invite deleted successfully');
        }
        dispatch(getFacilityInvites(facilityId));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 400) {
          toast.error(err?.response?.data?.message);
        }
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const updateDeviceAssociationWithFacility = (
  facilityId: number,
  action: FacilityDeviceAssociationAction,
  device_ids: number[],
  isFromModernFacility: boolean = false, // Skip loading state for modern facility UI to use modal's own loading state
) => {
  return (
    dispatch: ThunkDispatch<FacilityStore, void, AnyAction> & Dispatch,
  ) => {
    if (!isFromModernFacility) {
      dispatch(setLoadState(true));
    }
    const requestPayload = {
      action: action,
      device_ids,
    };
    return axios
      .put(
        formatUrl(restHost, `eng/facility/${facilityId}/sync_devices`),
        requestPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      )
      .then(() => {
        toast.success('Device to Facility sync completed');
        dispatch(setLoadState(false));
        dispatch(clearFacilityDevices());
      })
      .catch((err) => {
        if (err.response && err?.response?.status === 401) {
          deleteToken();
        }
        dispatch(apiError(err));
        dispatch(setLoadState(false));
        toast.error(err?.response?.data?.message);
      });
  };
};

export const populateCaregivers = (
  facilityId: number,
  caregivers: Caregiver[],
) => ({
  type: FacilityStoreActionTypes.POPULATE_CAREGIVERS,
  payload: { facilityId, caregivers },
});

export const getAllCaregivers = (
  facilityId: number,
  includeOwners: boolean = false,
) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .get(
        formatUrl(
          restHost,
          `facility/${facilityId}/customers?include_owners=${includeOwners}`,
        ),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      )
      .then((res) => {
        dispatch(populateCaregivers(facilityId, res.data));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const populateCaregiverById = (caregiver: CaregiverDetails) => ({
  type: FacilityStoreActionTypes.POPULATE_CAREGIVER_BY_ID,
  payload: { caregiver },
});

export const getCaregiverById = (customerId: number) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, `facility/customer/${customerId}`), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        const caregiverData = {
          ...res.data,
          combined_name: `${res.data.first_name} ${res.data.last_name}`,
        };
        dispatch(populateCaregiverById(caregiverData));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const updateCaregiverById = (
  customerId: number,
  data: UpdateCaregiverData,
) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const res = await axios.put(
        formatUrl(restHost, `facility/customer/${customerId}`),
        data,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      if (res.status === 200) {
        toast.success('Caregiver updated successfully', { autoClose: 3000 });
        dispatch({
          type: FacilityStoreActionTypes.UPDATE_CAREGIVER_BY_ID,
          payload: { caregiver: res.data },
        });
        dispatch(setUpdateCaregiverSuccess(true));
      } else {
        toast.error(
          ((res as any)?.response?.data as string) ||
            'Failed to update caregiver',
          {
            autoClose: 3000,
          },
        );
        dispatch(setUpdateCaregiverSuccess(false));
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to update caregiver', {
        autoClose: 3000,
      });
      dispatch(apiError(err));
      dispatch(setUpdateCaregiverSuccess(false));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const deleteCaregiverById = (customerId: number) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .delete(formatUrl(restHost, `customers/${customerId}`), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        if (!res.status) throw res;
        dispatch({
          type: FacilityStoreActionTypes.DELETE_CAREGIVER_BY_ID,
          payload: { customerId },
        });
        dispatch(setDeleteCaregiverSuccess(true));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error('Failed to delete user');
        dispatch(apiError(err));
        dispatch(setLoadState(false));
        dispatch(setDeleteCaregiverSuccess(false));
      });
  };
};

export const setDeleteCaregiverSuccess = (success: boolean) => ({
  type: FacilityStoreActionTypes.SET_DELETE_CAREGIVER_SUCCESS,
  payload: { success },
});

export const addCaregiver = (data: NewCaregiverData) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.post(
        formatUrl(restHost, `facility/customer`),
        data,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      if ((response as any)?.response?.status === 409) {
        toast.error('Customer already exists');
      } else {
        dispatch(setAddCaregiverSuccess(true));
      }
      dispatch(setLoadState(false));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        if (err.response.data && err.response.data) {
          toast.error('An error occurred. Please try again.');
        }
      }
      dispatch(apiError(err));
      dispatch(setLoadState(false));
      dispatch(setAddCaregiverSuccess(false));
    }
  };
};

export const inviteFacilityAdmin = (data: {
  email: string;
  facility_role_id: number;
  first_name?: string;
  last_name?: string;
}) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .post(formatUrl(restHost, `facility/invite`), data, {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          // Callers confirm the invite with their own success modal.
          dispatch(setInviteFacilityAdminSuccess(true));
          dispatch(setAddParentFacilityAdminStatus(true));
        } else {
          if ((response as any)?.response?.data) {
            toast.error((response as any)?.response?.data, {
              autoClose: 3000,
            });
          }
          dispatch(setInviteFacilityAdminSuccess(false));
          dispatch(setAddParentFacilityAdminStatus(false));
        }
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        // This endpoint answers with a bare string ("Customer already exists"),
        // so only reach for .message when the payload is an object. Callers
        // render the generic failure, so stay quiet when there is no reason.
        const reason =
          typeof err?.response?.data === 'string'
            ? err.response.data
            : err?.response?.data?.message;
        if (reason) {
          toast.error(reason, { autoClose: 3000 });
        }
        dispatch(apiError(err));
        dispatch(setInviteFacilityAdminSuccess(false));
        dispatch(setAddParentFacilityAdminStatus(false));
      })
      .finally(() => {
        dispatch(setLoadState(false));
      });
  };
};

export const getParentFacilities = () => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, 'eng/parent_facility'), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        dispatch(populateParentFacilities(res.data));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          deleteToken();
        }
        toast.error(
          err.response?.data?.message || 'Failed to fetch facilities',
        );
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const addParentFacilityAdmin = (
  parentFacilityId: number,
  data: AddParentFacilityAdminData,
) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.post(
        formatUrl(restHost, `eng/parent_facility/${parentFacilityId}/customer`),
        data,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if (response.status === 200) {
        dispatch(setAddParentFacilityAdminStatus(true));
      } else if (response.status === 409) {
        toast.error(
          (response as any)?.response?.data ||
            'Admin with this email already exists',
          {
            autoClose: 3000,
          },
        );
        dispatch(setAddParentFacilityAdminStatus(false));
      } else {
        toast.error('Failed to add facility admin', {
          autoClose: 3000,
        });
        dispatch(setAddParentFacilityAdminStatus(false));
      }
    } catch (err) {
      console.log({ err });
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          deleteToken();
        } else {
          toast.error(
            err.response?.data?.message || 'Failed to add facility admin',
            {
              autoClose: 3000,
            },
          );
        }
      }
      dispatch(apiError(err));
      dispatch(setAddParentFacilityAdminStatus(false));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const createParentFacility = (data: CreateParentFacilityData) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.post(
        formatUrl(restHost, 'eng/parent_facility'),
        data,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      if (response.status === 200) {
        return response.data.parent_facility_id;
      } else {
        toast.error(
          (response as any)?.response?.data ||
            'Failed to create parent facility',
        );
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          deleteToken();
        }
        toast.error(
          err.response?.data?.message || 'Failed to create parent facility',
        );
      }
      dispatch(apiError(err));
      throw err;
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

// PUT eng/parent_facility/:id is not implemented on the backend yet (OI-1235
// follow-up), so this thunk is wired but won't persist — the facility Save stays
// disabled until the endpoint ships.
export const updateParentFacility = (
  parentFacilityId: number,
  data: UpdateParentFacility,
) => {
  return (
    dispatch: ThunkDispatch<FacilityStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    return axios
      .put(
        formatUrl(restHost, `eng/parent_facility/${parentFacilityId}`),
        data,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      )
      .then(() => {
        dispatch(getParentFacilities());
        dispatch(setLoadState(false));
        return true;
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
        return false;
      });
  };
};

export const createLocation = (data: CreateLocationData) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.post(
        formatUrl(restHost, 'eng/facility'),
        data,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      if (response.status === 200) {
        toast.success('Location created successfully');
        return response.data;
      } else {
        toast.error(
          (response as any)?.response?.data || 'Failed to create location',
        );
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          deleteToken();
        }
        toast.error(err.response?.data?.message || 'Failed to create location');
      }
      dispatch(apiError(err));
      throw err;
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const getStaffList = (parentFacilityId: number, locationId?: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const url = locationId
        ? formatUrl(
            restHost,
            `eng/parent_facility/${parentFacilityId}/staff?location_id=${locationId}`,
          )
        : formatUrl(restHost, `eng/parent_facility/${parentFacilityId}/staff`);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      });
      dispatch(populateStaffList(response.data));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to fetch staff');
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const getAdminStaffList = (locationId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const url = formatUrl(restHost, `facility/${locationId}/staffs`);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      });
      dispatch(populateStaffList(response.data));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to fetch staff');
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const getPatientsList = (
  parentFacilityId: number,
  locationId?: number,
) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const url = locationId
        ? formatUrl(
            restHost,
            `eng/parent_facility/${parentFacilityId}/patients?location_id=${locationId}`,
          )
        : formatUrl(
            restHost,
            `eng/parent_facility/${parentFacilityId}/patients`,
          );
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      });
      dispatch(populatePatientsList(response.data));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to fetch patients');
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const getPatientsListByLocation = (locationId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.get(
        formatUrl(restHost, `facility/${locationId}/patients`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch(populatePatientsList(response.data));
    } catch (err) {
      toast.error('Failed to fetch patients');
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const getDevicesList = (
  parentFacilityId: number,
  locationId?: number,
) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const url = locationId
        ? formatUrl(
            restHost,
            `eng/parent_facility/${parentFacilityId}/devices?location_id=${locationId}`,
          )
        : formatUrl(
            restHost,
            `eng/parent_facility/${parentFacilityId}/devices`,
          );
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      });
      dispatch(populateDevicesList(response.data));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to fetch devices');
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const getNotesList = (parentFacilityId: number, locationId?: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const url = locationId
        ? formatUrl(
            restHost,
            `eng/parent_facility/${parentFacilityId}/note?location_id=${locationId}`,
          )
        : formatUrl(restHost, `eng/parent_facility/${parentFacilityId}/note`);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      });
      dispatch(populateNotesList(response.data));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to fetch notes');
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const getNotesListByLocation = (locationId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.get(
        formatUrl(restHost, `facility/${locationId}/note`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch(populateNotesList(response.data));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to fetch notes');
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const addNote = (
  note: string,
  parentFacilityId: number,
  locationId?: number,
) => {
  return async (dispatch: ThunkDispatch<FacilityStore, any, AnyAction>) => {
    dispatch(setLoadState(true));
    try {
      const data = {
        note,
        parent_facility_id: parentFacilityId,
        location_id: locationId,
      };
      const response = await axios.post(
        formatUrl(restHost, 'eng/facility/note'),
        data,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if (response.status === 200) {
        toast.success('Note added successfully');
        dispatch(getNotesList(parentFacilityId, locationId)); // Refresh notes
      } else {
        toast.error('Failed to add note');
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to add note');
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const addNoteByLocation = (
  note: string,
  locationId: number,
  parentFacilityId?: number,
) => {
  return async (dispatch: ThunkDispatch<FacilityStore, any, AnyAction>) => {
    dispatch(setLoadState(true));
    try {
      const payload: any = { note };
      if (parentFacilityId) payload.parent_facility_id = parentFacilityId;
      const response = await axios.post(
        formatUrl(restHost, `facility/${locationId}/note`),
        payload,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if (response.status === 200) {
        toast.success('Note added successfully');
        dispatch(getNotesListByLocation(locationId)); // Refresh notes for location
      } else {
        toast.error('Failed to add note');
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to add note');
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const editNote = (noteId: number, note: string) => {
  return async (dispatch: ThunkDispatch<FacilityStore, any, AnyAction>) => {
    dispatch(setLoadState(true));
    try {
      const data = { note, note_id: noteId };
      const response = await axios.put(
        formatUrl(restHost, 'eng/facility/note'),
        data,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if (response.status === 200) {
        dispatch(setNoteActionStatus(true, 'Note updated successfully!'));
      } else {
        toast.error('Failed to update note');
        dispatch(setNoteActionStatus(false, 'Failed to update note!'));
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      dispatch(setNoteActionStatus(false, 'Failed to update note!'));
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const editNoteByLocation = (
  locationId: number,
  noteId: number,
  note: string,
) => {
  return async (dispatch: ThunkDispatch<FacilityStore, any, AnyAction>) => {
    dispatch(setLoadState(true));
    try {
      const data = { note, note_id: noteId };
      const response = await axios.put(
        formatUrl(restHost, `facility/${locationId}/note`),
        data,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if (response.status === 200) {
        dispatch(setNoteActionStatus(true, 'Note updated successfully!'));
      } else {
        toast.error('Failed to update note');
        dispatch(setNoteActionStatus(false, 'Failed to update note!'));
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      dispatch(setNoteActionStatus(false, 'Failed to update note!'));
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const deleteNote = (noteId: number) => {
  return async (dispatch: ThunkDispatch<FacilityStore, any, AnyAction>) => {
    dispatch(setLoadState(true));
    try {
      const data = { note_id: noteId };
      const response = await axios.delete(
        formatUrl(restHost, 'eng/facility/note'),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
          data: data,
        },
      );

      if (response.status === 200) {
        dispatch(setNoteActionStatus(true, 'Note deleted successfully!'));
      } else {
        dispatch(setNoteActionStatus(false, 'Failed to delete note!'));
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      dispatch(setNoteActionStatus(false, 'Failed to delete note!'));
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const deleteNoteByLocation = (locationId: number, noteId: number) => {
  return async (dispatch: ThunkDispatch<FacilityStore, any, AnyAction>) => {
    dispatch(setLoadState(true));
    try {
      const data = { note_id: noteId };
      const response = await axios.delete(
        formatUrl(restHost, `facility/${locationId}/note`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
            'Content-Type': 'application/json',
          },
          data,
        },
      );

      if (response.status === 200) {
        dispatch(setNoteActionStatus(true, 'Note deleted successfully!'));
      } else {
        dispatch(setNoteActionStatus(false, 'Failed to delete note!'));
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      dispatch(setNoteActionStatus(false, 'Failed to delete note!'));
      dispatch(apiError(err));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const deactivateParentFacility = (parentFacilityId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.post(
        formatUrl(
          restHost,
          `eng/parent_facility/${parentFacilityId}/deactivate`,
        ),
        {},
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if (response.status === 200) {
        dispatch(setDeactivateFacilityStatus(true));
        return true;
      } else {
        dispatch(setDeactivateFacilityStatus(false));
        return false;
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      dispatch(setDeactivateFacilityStatus(false));
      toast.error('Failed to deactivate facility');
      return false;
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const updateStaffDetails =
  (
    facility_id: number,
    customer_id: number,
    data: { first_name?: string; last_name?: string; new_role: string },
    showToast?: boolean,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.put(
        formatUrl(
          restHost,
          `eng/facility/${facility_id}/customer/${customer_id}`,
        ),
        data,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      if (response.status === 200) {
        dispatch(setEditStaffStatus(true));
      } else {
        if (showToast) {
          // showing toast if no succes or failed models used
          toast.error(
            ((response as any)?.response?.data as string) || 'Failed to update',
            {
              autoClose: 3000,
            },
          );
        }
        dispatch(setEditStaffStatus(false));
      }
    } catch (err) {
      dispatch(setEditStaffStatus(false));
      dispatch(apiError(err));
      if (showToast) {
        toast.error('Failed to update', {
          autoClose: 3000,
        });
      }
    } finally {
      dispatch(setLoadState(false));
    }
  };

export const deactivateLocation = (facilityId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.post(
        formatUrl(restHost, `eng/facility/${facilityId}/deactivate`),
        {},
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if (response.status === 200) {
        dispatch(setDeactivateFacilityStatus(true));
        return true;
      } else {
        dispatch(setDeactivateFacilityStatus(false));
        return false;
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deleteToken();
      }
      dispatch(setDeactivateFacilityStatus(false));
      toast.error('Failed to deactivate location');
      return false;
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const setInviteFacilityAdminSuccess = (success: boolean) => ({
  type: FacilityStoreActionTypes.SET_INVITE_FACILITY_ADMIN_SUCCESS,
  payload: { success },
});

export const setAddCaregiverSuccess = (success: boolean) => ({
  type: FacilityStoreActionTypes.SET_ADD_CAREGIVER_SUCCESS,
  payload: { success },
});

export const resetAddCaregiverSuccess = () => ({
  type: FacilityStoreActionTypes.RESET_ADD_CAREGIVER_SUCCESS,
});

export const setUpdateCaregiverSuccess = (success: boolean) => ({
  type: FacilityStoreActionTypes.SET_UPDATE_CAREGIVER_SUCCESS,
  payload: { success },
});

export const resetSuccessStates = () => ({
  type: FacilityStoreActionTypes.RESET_SUCCESS_STATES,
});

// Sequence id for survey insights requests: the reducer drops responses that
// a newer request (another location or window) has superseded.
let facilitySurveyInsightsRequestSeq = 0;

export const requestFacilitySurveyInsights = (
  facilityId: number,
  requestedDays: number,
  granularity: SurveyInsightsGranularity,
  requestId: number,
): RequestFacilitySurveyInsightsAction => ({
  type: FacilityStoreActionTypes.REQUEST_FACILITY_SURVEY_INSIGHTS,
  payload: { facilityId, requestedDays, granularity, requestId },
});

export const fetchFacilitySurveyInsightsSuccess = (
  insights: FacilitySurveyInsightsState,
  requestId: number,
): FetchFacilitySurveyInsightsSuccessAction => ({
  type: FacilityStoreActionTypes.FETCH_FACILITY_SURVEY_INSIGHTS_SUCCESS,
  payload: { ...insights, requestId },
});

export const fetchFacilitySurveyInsightsFailure = (
  requestId: number,
  error: string,
): FetchFacilitySurveyInsightsFailureAction => ({
  type: FacilityStoreActionTypes.FETCH_FACILITY_SURVEY_INSIGHTS_FAILURE,
  payload: { requestId, error },
});

// GET /facility/:id/survey_insights for a rolling window of `days` ending
// today in `timeZone` (the browser's), all survey types in one call.
export const fetchFacilitySurveyInsights = (
  facilityId: number,
  days: number,
  granularity: SurveyInsightsGranularity,
  timeZone: string,
) => {
  return async (dispatch: Dispatch) => {
    const requestId = ++facilitySurveyInsightsRequestSeq;
    dispatch(
      requestFacilitySurveyInsights(facilityId, days, granularity, requestId),
    );
    try {
      const response = await restClient.facility.getFacilitySurveyInsights(
        facilityId,
        days,
        granularity,
        undefined,
        timeZone,
      );
      dispatch(
        fetchFacilitySurveyInsightsSuccess(
          {
            ...(response as FacilitySurveyInsights),
            facilityId,
            requestedDays: days,
          },
          requestId,
        ),
      );
    } catch (err: any) {
      const message = getApiErrorMessage(
        err,
        'Failed to fetch survey insights',
      );
      toast.error(message);
      dispatch(fetchFacilitySurveyInsightsFailure(requestId, message));
    }
  };
};
